/* EC page builder — shared behaviour for ec-* sections. Vanilla JS, no deps. */
(() => {
  if (window.__ecBase) return; // every ec-* section includes this file; run once
  window.__ecBase = true;
  const define = (name, cls) => customElements.get(name) || customElements.define(name, cls);
  const desktop = window.matchMedia('(min-width: 992px)');

  /* <ec-carousel data-autoplay="4000" data-mobile-only data-loop data-start-middle>
       .ec-car__track > slides, [data-prev] [data-next] .ec-car__dots */
  define('ec-carousel', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.ec-car__track');
      if (!this.track) return;
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.dots = this.querySelector('.ec-car__dots');
      this.loop = this.hasAttribute('data-loop');
      this.prev?.addEventListener('click', () => this.go(this.index() - 1, true));
      this.next?.addEventListener('click', () => this.go(this.index() + 1, true));
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this._raf);
        this._raf = requestAnimationFrame(() => this.update());
      }, { passive: true });
      new ResizeObserver(() => this.build()).observe(this.track);
      this.build();
      if (this.hasAttribute('data-start-middle')) {
        const s = this.slides(), m = s[Math.floor(s.length / 2)];
        if (m) this.track.scrollLeft = m.offsetLeft - s[0].offsetLeft;
      }
      const ms = Number(this.dataset.autoplay || 0);
      if (ms > 0) {
        this.style.setProperty('--ec-autoplay', ms + 'ms');
        this._timer = setInterval(() => {
          if (!this.matches(':hover') && !this.contains(document.activeElement)) this.go(this.index() + 1);
        }, ms);
      }
    }
    disconnectedCallback() { clearInterval(this._timer); }
    slides() { return [...this.track.children].filter((s) => s.offsetParent !== null || s.getClientRects().length); }
    active() { return !this.hasAttribute('data-mobile-only') || !desktop.matches; }
    pages() {
      // number of snap positions that can actually be reached
      const max = this.track.scrollWidth - this.track.clientWidth;
      const s = this.slides();
      const reachable = s.filter((el) => el.offsetLeft - s[0].offsetLeft <= max + 2).length;
      return Math.max(1, reachable);
    }
    index() {
      const s = this.slides();
      if (!s.length) return 0;
      const x = this.track.scrollLeft + s[0].offsetLeft;
      let best = 0;
      s.forEach((el, i) => { if (Math.abs(el.offsetLeft - x) < Math.abs(s[best].offsetLeft - x)) best = i; });
      return Math.min(best, this.pages() - 1);
    }
    go(i, user) {
      const n = this.pages();
      if (i >= n) i = this.loop || !user ? 0 : n - 1;
      if (i < 0) i = this.loop ? n - 1 : 0;
      const s = this.slides();
      if (!s[i]) return;
      this.track.scrollTo({ left: s[i].offsetLeft - s[0].offsetLeft, behavior: 'smooth' });
    }
    build() {
      if (!this.dots) return this.update();
      const n = this.active() ? this.pages() : 0;
      if (this.dots.children.length !== n) {
        this.dots.innerHTML = '';
        for (let i = 0; i < n; i++) {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'ec-car__dot';
          b.setAttribute('aria-label', 'Go to slide ' + (i + 1));
          b.addEventListener('click', () => this.go(i, true));
          this.dots.appendChild(b);
        }
      }
      this.update();
    }
    update() {
      const i = this.index();
      [...(this.dots?.children || [])].forEach((d, k) => d.classList.toggle('is-active', k === i));
      if (!this.loop) {
        if (this.prev) this.prev.disabled = i <= 0;
        if (this.next) this.next.disabled = i >= this.pages() - 1;
      }
    }
  });

  /* Accordion: [data-ec-acc] wrapper, [data-ec-acc-trigger] button */
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-ec-acc-trigger]');
    if (!t) return;
    const acc = t.closest('[data-ec-acc]');
    const open = acc.dataset.open !== 'true';
    acc.dataset.open = String(open);
    t.setAttribute('aria-expanded', String(open));
  });

  /* Tabs: [data-ec-tabs] > [role=tab][aria-controls], [role=tabpanel] */
  document.addEventListener('click', (e) => {
    const tab = e.target.closest('[data-ec-tabs] [role="tab"]');
    if (!tab) return;
    const root = tab.closest('[data-ec-tabs]');
    root.querySelectorAll('[role="tab"]').forEach((t) => {
      const on = t === tab;
      t.setAttribute('aria-selected', String(on));
      t.tabIndex = on ? 0 : -1;
      const panel = root.querySelector('#' + t.getAttribute('aria-controls'));
      if (panel) panel.hidden = !on;
    });
  });

  /* Rotating announcement slides */
  define('ec-rotator', class extends HTMLElement {
    connectedCallback() {
      const slides = [...this.children];
      if (slides.length < 2) return;
      let i = 0;
      this._t = setInterval(() => {
        slides[i].classList.remove('is-active');
        i = (i + 1) % slides.length;
        slides[i].classList.add('is-active');
      }, Number(this.dataset.speed || 5000));
    }
    disconnectedCallback() { clearInterval(this._t); }
  });

  /* Sticky CTA: shown while no .ec-hide-cta element is on screen */
  define('ec-sticky-cta', class extends HTMLElement {
    connectedCallback() {
      const check = () => {
        const visible = [...document.querySelectorAll('.ec-hide-cta')].some((el) => {
          const r = el.getBoundingClientRect();
          return r.top < window.innerHeight && r.bottom > 0;
        });
        this.classList.toggle('is-stuck', !visible);
      };
      this._io = new IntersectionObserver(check);
      document.querySelectorAll('.ec-hide-cta').forEach((el) => this._io.observe(el));
      check();
    }
    disconnectedCallback() { this._io?.disconnect(); }
  });
})();
